import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export enum NewsTickerSpeed {
  SLOW = 'slow',
  NORMAL = 'normal',
  FAST = 'fast'
}

export interface UserPreferences {
  newsTickerSpeed: NewsTickerSpeed;
}

const DEFAULT_PREFERENCES: UserPreferences = {
  newsTickerSpeed: NewsTickerSpeed.NORMAL
};

@Injectable({
  providedIn: 'root'
})
export class PreferencesService {
  private readonly STORAGE_KEY = 'user_preferences';
  private preferencesSubject: BehaviorSubject<UserPreferences>;

  constructor() {
    const savedPrefs = this.loadPreferences();
    this.preferencesSubject = new BehaviorSubject<UserPreferences>(savedPrefs);
  }

  private loadPreferences(): UserPreferences {
    try {
      const saved = localStorage.getItem(this.STORAGE_KEY);
      if (saved) {
        return { ...DEFAULT_PREFERENCES, ...JSON.parse(saved) };
      }
    } catch (error) {
      console.error('Erreur lors du chargement des préférences:', error);
    }
    return DEFAULT_PREFERENCES;
  }

  private savePreferences(prefs: UserPreferences): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(prefs));
      this.preferencesSubject.next(prefs);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde des préférences:', error);
    }
  }

  getPreferences() {
    return this.preferencesSubject.asObservable();
  }

  getCurrentPreferences(): UserPreferences {
    return this.preferencesSubject.value;
  }

  setNewsTickerSpeed(speed: NewsTickerSpeed): void {
    const current = this.getCurrentPreferences();
    this.savePreferences({ ...current, newsTickerSpeed: speed });
  }

  getNewsTickerSpeed(): NewsTickerSpeed {
    return this.getCurrentPreferences().newsTickerSpeed;
  }

  // Conversion vitesse -> pixels par seconde
  getNewsTickerPixelsPerSecond(screenWidth: number): number {
    const speed = this.getNewsTickerSpeed();

    // Vitesse normale = vitesse de lecture confortable
    // Lent = 0.4x normal, Rapide = 1.2x normal
    let baseNormal = 100;

    if (screenWidth < 600) {
      baseNormal = 80;
    } else if (screenWidth > 1600) {
      baseNormal = 120;
    }

    switch (speed) {
      case NewsTickerSpeed.SLOW:
        return Math.round(baseNormal * 0.4);
      case NewsTickerSpeed.FAST:
        return Math.round(baseNormal * 1.2);
      case NewsTickerSpeed.NORMAL:
      default:
        return baseNormal;
    }
  }
}
